import { gql } from '@apollo/client';
import { Box, Card, Container, Flex, Separator, Wrapper } from '@energiebespaarders/symbols';
import { Heading, Small } from '@energiebespaarders/symbols/helpers';
import { margin, themify } from '@energiebespaarders/symbols/styles/mixins';
import dayjs from 'dayjs';
import Head from 'next/head';
import React from 'react';
import { Image } from 'react-datocms';
import styled from 'styled-components';
import DatoMetaTags from '~/components/DatoCms/DatoMetaTags';
import type { CMSStructuredTextData } from '~/components/DatoCms/TypedStructuredText';
import TypedStructuredText from '~/components/DatoCms/TypedStructuredText';
import HybridLink from '~/components/HybridLink';
import Breadcrumbs from '~/components/_blog/BreadCrumbs';
import config from '~/config';
import type {
  getBlogPostBySlug,
  getBlogPostBySlug_allBlogPosts,
  getBlogPostBySlugVariables,
} from '~/types/generated/cms/getBlogPostBySlug';
import { BlogPostFragment } from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import { allBlogPostSlugs } from '..';
import type { GetStaticPaths, GetStaticProps } from 'next/types';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

interface BlogPostPageProps {
  blogPost: getBlogPostBySlug_allBlogPosts;
}

const GET_BLOG_POST_BY_SLUG = gql`
  ${BlogPostFragment}
  query getBlogPostBySlug($slug: String!) {
    allBlogPosts(filter: { slug: { eq: $slug } }) {
      ...BlogPostFragment
    }
  }
`;

const blogPostBySlug = async (
  slug: string,
  client: ApolloClient<NormalizedCacheObject>,
): Promise<getBlogPostBySlug_allBlogPosts | undefined> => {
  const { data } = await client.query<getBlogPostBySlug, getBlogPostBySlugVariables>({
    query: GET_BLOG_POST_BY_SLUG,
    variables: { slug: slug },
  });

  return data.allBlogPosts[0];
};

export const HeroWrapper = styled.div`
  position: relative;
  height: 420px;
  overflow: hidden;
  z-index: 1;
`;

const StyledHeroImage = styled(Image)`
  filter: blur(2px);
  max-width: unset !important;

  picture img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }
`;

const StyledCategory = styled.a`
  color: ${x => x.theme.colors.grayDark};
  font-size: ${x => x.theme.type.scale[7]};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  border-bottom: 2px solid ${x => x.theme.colors.green};
  display: inline-block;
  ${margin(2, 'r')}
  ${margin(2, 'b')}
`;

const BlogPost: React.FC<BlogPostPageProps> = ({ blogPost }) => {
  const canonicalUrl = `https://energiebespaarders.nl/blog/${blogPost.slug}`;

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blogPost.title,
    datePublished: blogPost.firstPublishedAt || blogPost._publishedAt,
    dateModified: blogPost._updatedAt,
    author: {
      '@type': 'Person',
      name: blogPost.author?.name || 'Onbekend',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Energiebespaarders',
      logo: {
        '@type': 'ImageObject',
        url: 'https://energiebespaarders.nl/img/logomark.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    image: blogPost.image?.responsiveImage?.src,
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdData) }}
        />
      </Head>

      <DatoMetaTags metaTags={blogPost.seo} canonicalUrl={canonicalUrl} />
      <Wrapper forceFullHeight bgColor="floral">
        <HeroWrapper>
          <StyledHeroImage data={blogPost.image!.responsiveImage!} />
        </HeroWrapper>
        <Container size="lg" py={10}>
          <Flex flexWrap="wrap" p={2} justifyContent="center">
            <Box width={1} mt={6}>
              <div
                style={{
                  position: 'relative',
                  marginTop: -240,
                  zIndex: 100,
                }}
              >
                <Breadcrumbs withBgTab />
              </div>
              <Card
                style={{
                  position: 'relative',
                  zIndex: 100,
                  borderRadius: '0 6px 6px',
                }}
                p={0}
              >
                <Flex flexWrap="wrap">
                  <Box width={1} px={[3, 5, 10]} pt={[3, 5, 10]}>
                    <HybridLink to={`/blog/category/${blogPost.category?.slug}`} prefetch={false}>
                      <StyledCategory>{blogPost.category!.name}</StyledCategory>
                    </HybridLink>
                    <Heading heading="h1" letterSpacing="-1px" lineHeight="1.15">
                      {blogPost.title}
                    </Heading>
                  </Box>
                  <Box width={1} px={[3, 5, 10]}>
                    <Small>
                      <p style={{ color: themify('grayDark') }}>
                        Geschreven door {blogPost.author?.name || 'Onbekend'} – op{' '}
                        {dayjs(blogPost.firstPublishedAt || blogPost._publishedAt).format(
                          'D MMMM YYYY',
                        )}
                      </p>
                    </Small>
                    <Separator my={5} />
                  </Box>

                  <Box width={1} px={[3, 5, 10]} pb={[3, 5, 10]}>
                    <TypedStructuredText
                      data={blogPost.structuredText as unknown as CMSStructuredTextData}
                    />
                  </Box>

                  {/* TODO: Fix
                  <Box width={1} px={[3, 5, 10]} pb={[3, 5, 10]}>
                    <p>
                      <Medium>Deel dit artikel met vrienden en familie!</Medium>
                    </p>
                    <SocialSharer title={blogPost.title!} />
                  </Box> */}
                </Flex>
              </Card>
            </Box>
          </Flex>
        </Container>
      </Wrapper>
    </>
  );
};

export default BlogPost;

export const getStaticProps: GetStaticProps<BlogPostPageProps> = async ({ params, preview }) => {
  if (!params) return { notFound: true };
  const client = getDatoClient(preview);

  const blogPost = await blogPostBySlug(params.slug as string, client);

  if (!blogPost) {
    return { notFound: true };
  }

  return {
    props: {
      blogPost,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (config.build.skipStaticGeneration) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const blogPosts = (await allBlogPostSlugs()).map(b => ({ params: { slug: b.slug! } }));
  return {
    paths: blogPosts,
    fallback: 'blocking',
  };
};
